stimulusApp.register(
  "search--group-configuration",
  class extends Stimulus.Controller {
    static targets = [
      "pane",
      "groupsContainer",
      "summary",
      "label",
      "groupTemplate",
      "addGroupButton",
      "mobileGuestsSubtitle",
    ]

    static values = {
      i18n: Object,
      showValidation: { type: Boolean, default: false },
      maxGroups: { type: Number, default: 10 },
      mobileOverlay: { type: Boolean, default: false },
      title: String,
    }

    static outlets = ["search--group-card"]

    #locale = "en"

    connect() {
      if (!this.hasGroupTemplateTarget) {
        console.error(
          "Group template target is required for search/group_configuration_controller",
        )
      }

      this.#locale = window.locale || "en"
      this.#refreshSummary(this.searchGroupCardOutlets)
    }

    searchGroupCardOutletConnected(card) {
      this.refresh()
      card.setValidation(this.showValidationValue)
    }

    searchGroupCardOutletDisconnected() {
      this.refresh()
    }

    open() {
      this.paneTarget.classList.add("place-search-expanded_opened")
      if (this.#isOverlayPane()) {
        this.paneTarget.classList.add("overlay_opened")
      }
      if (this.titleValue) {
        window.ReactNativeWebView?.postMessage(
          JSON.stringify({ title: this.titleValue }),
        )
      }
    }

    close() {
      this.paneTarget.classList.remove("place-search-expanded_opened")
      if (this.#isOverlayPane()) {
        this.paneTarget.classList.remove("overlay_opened")
      }
    }

    validate() {
      return this.#allGroupsValid()
    }

    showValidation() {
      this.showValidationValue = true
      this.#openInvalidGroups()
    }

    refresh() {
      const cards = this.searchGroupCardOutlets
      this.#syncGroupNumbers(cards)
      this.#refreshSummary(cards)
      this.#syncAddGroupButton(cards)
      this.#syncSingleGroupClass(cards)
      if (cards.length === 1) {
        cards[0].open()
      }
    }

    requestClose() {
      if (this.mobileOverlayValue && !this.#allGroupsValid()) {
        this.showValidation()
        this.#scrollToFirstInvalid()
        return
      }
      this.closeWithoutValidation()
    }

    closeWithoutValidation() {
      this.element.dispatchEvent(
        new CustomEvent("hb:search-form:close", { bubbles: true }),
      )
    }

    showValidationValueChanged() {
      this.searchGroupCardOutlets.forEach((card) =>
        card.setValidation(this.showValidationValue),
      )
    }

    addGroup() {
      if (!this.hasGroupTemplateTarget || !this.hasGroupsContainerTarget) return
      const cards = this.searchGroupCardOutlets
      if (cards.length >= this.maxGroupsValue) return

      cards.forEach((card) => card.close())
      const fragment = this.groupTemplateTarget.content.cloneNode(true)
      this.groupsContainerTarget.appendChild(fragment)
    }

    #groupSummary(state) {
      return HB.groupSummary({
        adults: state.adults,
        childrenAges: state.childrenAges,
        i18n: this.i18nValue,
        locale: this.#locale,
      })
    }

    #allGroupsValid() {
      return this.searchGroupCardOutlets.every((card) => card.isValid())
    }

    #refreshSummary(cards) {
      const states = cards.map((card) => card.state()).filter(Boolean)
      const summaryText = this.#overallSummary(states)
      if (this.hasSummaryTarget) {
        this.summaryTarget.textContent = summaryText
      }
      if (this.hasLabelTarget) {
        this.labelTarget.textContent = summaryText
      }
      this.#syncMobileGuestsSubtitle(states)
    }

    #overallSummary(states) {
      if (states.length === 1) {
        return this.#groupSummary(states[0])
      }
      const totalGuests = this.#totalGuests(states)
      const guestsLabel = HB.pluralize(
        totalGuests,
        this.i18nValue?.guests,
        this.#locale,
      )
      const groupsLabel = HB.pluralize(
        states.length,
        this.i18nValue?.groups,
        this.#locale,
      )
      return `${guestsLabel} (${groupsLabel})`
    }

    #totalGuests(states) {
      return states.reduce(
        (sum, state) => sum + state.adults + state.childrenAges.length,
        0,
      )
    }

    #syncGroupNumbers(cards) {
      cards.forEach((card, index) => {
        card.setIndex(index)
      })
    }

    #syncAddGroupButton(cards) {
      if (!this.hasAddGroupButtonTarget) return
      const shouldHide = cards.length >= this.maxGroupsValue
      this.addGroupButtonTarget.classList.toggle("hidden", shouldHide)
    }

    #syncSingleGroupClass(cards) {
      if (!this.hasGroupsContainerTarget) return
      this.groupsContainerTarget.classList.toggle(
        "search-groups_single",
        cards.length === 1,
      )
    }

    #syncMobileGuestsSubtitle(states) {
      if (!this.mobileOverlayValue || !this.hasMobileGuestsSubtitleTarget)
        return
      this.mobileGuestsSubtitleTarget.textContent = HB.pluralize(
        this.#totalGuests(states),
        this.i18nValue?.guests,
        this.#locale,
      )
    }

    #scrollToFirstInvalid() {
      const firstInvalidCard = this.#invalidCards()[0]
      firstInvalidCard?.element?.scrollIntoView?.({
        behavior: "smooth",
        block: "center",
      })
    }

    #openInvalidGroups() {
      this.#invalidCards().forEach((card) => card.open())
    }

    #invalidCards() {
      return this.searchGroupCardOutlets.filter((card) => !card.isValid())
    }

    #isOverlayPane() {
      return (
        this.mobileOverlayValue && this.paneTarget.classList.contains("overlay")
      )
    }
  },
)
